import { ScreenFieldsEdit } from '../../../../types/form-types'
import { SetValue, ValidationErrors } from '../../../hooks/use-edit-form'
import LookupField from '../../wells/lookup-field'
import { preventChangeOnScroll } from '../../../services/form-utils'
import { FormField, FormFieldRow, TextInput } from '@msaf/core-react'
import { useLookup } from '../../../hooks/use-api'
import { ScreenType } from '../../../services/constants'
import { LookupData } from '../../../../types/api-types'
import { useState } from 'react'

interface WellConstructionScreensEditInterface {
  fields: Partial<ScreenFieldsEdit>
  index?: number
  isLoading: boolean
  setValue: SetValue<ScreenFieldsEdit>
  validationErrors: ValidationErrors
}

export default function WellConstructionScreensEdit({
  fields,
  isLoading,
  setValue,
  validationErrors,
}: WellConstructionScreensEditInterface) {
  const [renderKey, setRenderKey] = useState(Date.now())
  const { data: screenTypeLookupValues } = useLookup('screenType')
  const screenTypeId = fields.screenType
  const screenTypeLookupValue = screenTypeLookupValues?.find(({ id }: LookupData) => id === screenTypeId)
  const isNoScreenScreenType = screenTypeLookupValue?.name === ScreenType.NO_SCREEN
  const isOpenHoleScreenType = screenTypeLookupValue?.name === ScreenType.OPEN_HOLE

  if (isOpenHoleScreenType || isNoScreenScreenType) {
    fields.screenLeaderTop = undefined
    fields.sumpBottom = undefined
    fields.screenInsideDiameter = undefined
    fields.slotSize = undefined
    fields.slotLength = undefined
  }

  if (isNoScreenScreenType) {
    fields.screenTop = undefined
    fields.screenBottom = undefined
  }

  const handleLookupChange = (value: string) => {
    /*
      Currently state management in the WellsNZ system is handled by setValue.
      When data is changed in a component (furthest child), state is passed up
      and this triggers a re-render of the entire component tree. Keys are used
      to manage the state/render cycles. Using the setRenderKey we can control
      render lifecycle and prevent the loss of focus on input fields when a value
      is being updated by a user.
    */
    setRenderKey(Date.now())
    setValue('screenType', value)
  }

  return (
    <>
      <FormField isSkeleton={isLoading} label='Type' labelId={`screenType-${fields.id}`}>
        <LookupField
          isSkeleton={isLoading}
          labelledBy={`screenType-${fields.id}`}
          fieldId='screenType'
          value={fields.screenType ?? ''}
          setValue={({ value }) => handleLookupChange(value)}
        />
      </FormField>
      <FormFieldRow>
        <FormField
          isSkeleton={isLoading}
          label='Top(m)'
          htmlFor={`screenTop-${fields.id}`}
          validationMessages={validationErrors?.screenTop}
        >
          <TextInput
            id={`screenTop-${fields.id}`}
            key={`screenTop-${renderKey}`}
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            value={fields.screenTop ?? ''}
            describedBy='screens-group'
            setValue={(value) => setValue('screenTop', value)}
            isInvalid={!!validationErrors?.screenTop}
            isDisabled={isNoScreenScreenType}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          label='Bottom(m)'
          htmlFor={`screenBottom-${fields.id}`}
          validationMessages={validationErrors?.screenBottom}
        >
          <TextInput
            id={`screenBottom-${fields.id}`}
            key={`screenBottom-${renderKey}`}
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            value={fields.screenBottom ?? ''}
            describedBy='screens-group'
            setValue={(value) => setValue('screenBottom', value)}
            isInvalid={!!validationErrors?.screenBottom}
            isDisabled={isNoScreenScreenType}
          />
        </FormField>
      </FormFieldRow>

      <FormFieldRow>
        <FormField
          isSkeleton={isLoading}
          label='Screen leader top(m)'
          htmlFor={`screenLeaderTop-${fields.id}`}
          validationMessages={validationErrors?.screenLeaderTop}
        >
          <TextInput
            id={`screenLeaderTop-${fields.id}`}
            key={`screenLeaderTop-${renderKey}`}
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            value={fields.screenLeaderTop ?? ''}
            describedBy='screens-group'
            setValue={(value) => setValue('screenLeaderTop', value)}
            isInvalid={!!validationErrors?.screenLeaderTop}
            isDisabled={isOpenHoleScreenType || isNoScreenScreenType}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          label='Sump bottom (m)'
          htmlFor={`sumpBottom-${fields.id}`}
          validationMessages={validationErrors?.sumpBottom}
        >
          <TextInput
            id={`sumpBottom-${fields.id}`}
            key={`sumpBottom-${renderKey}`}
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            value={fields.sumpBottom ?? ''}
            describedBy='screens-group'
            setValue={(value) => setValue('sumpBottom', value)}
            isInvalid={!!validationErrors?.sumpBottom}
            isDisabled={isOpenHoleScreenType || isNoScreenScreenType}
          />
        </FormField>
      </FormFieldRow>

      <FormFieldRow>
        <FormField
          isSkeleton={isLoading}
          label='Screen inside diameter (mm)'
          htmlFor={`screenInsideDiameter-${fields.id}`}
          validationMessages={validationErrors?.screenInsideDiameter}
        >
          <TextInput
            id={`screenInsideDiameter-${fields.id}`}
            key={`screenInsideDiameter-${renderKey}`}
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            value={fields.screenInsideDiameter ?? ''}
            describedBy='screens-group'
            setValue={(value) => setValue('screenInsideDiameter', value)}
            isInvalid={!!validationErrors?.screenInsideDiameter}
            isDisabled={isOpenHoleScreenType || isNoScreenScreenType}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          label='Slot size (mm)'
          htmlFor={`slotSize-${fields.id}`}
          validationMessages={validationErrors?.slotSize}
        >
          <TextInput
            id={`slotSize-${fields.id}`}
            key={`slotSize-${renderKey}`}
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            value={fields.slotSize ?? ''}
            describedBy='screens-group'
            setValue={(value) => setValue('slotSize', value)}
            isInvalid={!!validationErrors?.slotSize}
            isDisabled={isOpenHoleScreenType || isNoScreenScreenType}
          />
        </FormField>
      </FormFieldRow>
      <FormFieldRow>
        <FormField
          isSkeleton={isLoading}
          label='Slot length (mm)'
          htmlFor={`slotLength-${fields.id}`}
          validationMessages={validationErrors?.slotLength}
        >
          <TextInput
            id={`slotLength-${fields.id}`}
            key={`slotLength-${renderKey}`}
            isSkeleton={isLoading}
            type='number'
            onWheel={preventChangeOnScroll}
            value={fields.slotLength ?? ''}
            describedBy='screens-group'
            setValue={(value) => setValue('slotLength', value)}
            isInvalid={!!validationErrors?.slotLength}
            isDisabled={isOpenHoleScreenType || isNoScreenScreenType}
          />
        </FormField>
      </FormFieldRow>
    </>
  )
}
